import React, { useEffect, useState, useCallback } from 'react';
import { supabaseAdmin } from '../config/supabase';
import toast from 'react-hot-toast';
import BlockEditor from '../components/BlockEditor';
import './NewsManagement.css';
import './ContactManagement.css';

import { storagePublicBaseUrl } from '../config/env';
import { isValidContactPhotoInput, normalizeContactPhotoUrl } from '../utils/contactPhotoUrl';
import {
  canUploadContactPhotosFromPanel,
  formatStorageUploadError,
  getStorageUploadBlockedMessage,
} from '../utils/storageUpload';
import { getSupabaseConnectionMode } from '../config/supabase';

const CONTACT_OFFICE_HEADER_KEY = 'contactOfficeHeader';

// Helper function to convert relative image path to full public URL
function getPublicImageUrl(path) {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  const cleanPath = path.startsWith('/') ? path.substring(1) : path;
  return `${storagePublicBaseUrl}/storage/v1/object/public/contact-photos/${cleanPath}`;
}

export default function ContactManagement() {
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedReferat, setSelectedReferat] = useState(null);
  const [persons, setPersons] = useState([]);
  /** Stanowiska urzędnicze (bez osoby) — tabela contact_office_positions. */
  const [officePositions, setOfficePositions] = useState([]);
  const [officePositionsTableReady, setOfficePositionsTableReady] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [showPersonModal, setShowPersonModal] = useState(false);
  const [showOfficeModal, setShowOfficeModal] = useState(false);
  const [editingOffice, setEditingOffice] = useState(null);
  const [officeFormData, setOfficeFormData] = useState({
    title: '',
    phone_number: '',
    phone_extension: '',
    email: '',
    sort_order: 0,
    active: true,
  });
  const [editingSection, setEditingSection] = useState(null);
  const [editingPerson, setEditingPerson] = useState(null);
  const [sectionFormData, setSectionFormData] = useState({
    name: '',
    order: 0,
    active: true,
    parent_id: null,
  });
  const [personFormData, setPersonFormData] = useState({
    first_name: '',
    last_name: '',
    function_role: '',
    gender: 'male',
    phone_number: '',
    phone_extension: '',
    email: '',
    room_number: '',
    order: 0,
    active: true,
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoUrlInput, setPhotoUrlInput] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [blocks, setBlocks] = useState([{ 
    type: 'text', 
    text: '', 
    align: 'left',
    bold: false,
    italic: false,
    underline: false
  }]);
  const [contactOfficeHeader, setContactOfficeHeader] = useState('');
  const [savingContactOfficeHeader, setSavingContactOfficeHeader] = useState(false);

  const loadContactOfficeHeader = useCallback(async () => {
    try {
      const { data, error } = await supabaseAdmin
        .from('app_settings')
        .select('setting_value')
        .eq('setting_key', CONTACT_OFFICE_HEADER_KEY)
        .maybeSingle();

      if (error) throw error;
      const v = data?.setting_value;
      if (v == null) {
        setContactOfficeHeader('');
        return;
      }
      setContactOfficeHeader(typeof v === 'string' ? v : '');
    } catch (error) {
      console.error('Error loading contact office header:', error);
    }
  }, []);

  const handleSaveContactOfficeHeader = async () => {
    setSavingContactOfficeHeader(true);
    try {
      const { error } = await supabaseAdmin
        .from('app_settings')
        .upsert(
          {
            setting_key: CONTACT_OFFICE_HEADER_KEY,
            setting_value: contactOfficeHeader.trim(),
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'setting_key' },
        );

      if (error) throw error;
      toast.success('Adres / nagłówek kontaktu zapisany');
    } catch (error) {
      toast.error(error.message || 'Nie udało się zapisać');
      console.error(error);
    } finally {
      setSavingContactOfficeHeader(false);
    }
  };

  const loadSections = useCallback(async () => {
    try {
      const { data, error } = await supabaseAdmin
        .from('contact_sections')
        .select('*')
        .order('order', { ascending: true });

      if (error) throw error;
      setSections(data || []);
      const topLevel = (data || []).filter((s) => !s.parent_id);
      if (topLevel.length > 0 && !selectedSection) {
        setSelectedSection(topLevel[0]);
      }
    } catch (error) {
      toast.error('Błąd podczas ładowania sekcji');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [selectedSection]);

  useEffect(() => {
    loadSections();
    loadContactOfficeHeader();
  }, [loadSections, loadContactOfficeHeader]);

  const topLevelSections = sections.filter((s) => !s.parent_id);
  const referaty = selectedSection ? sections.filter((s) => s.parent_id === selectedSection.id) : [];
  const showReferatyColumn = selectedSection?.name && selectedSection.name.toLowerCase().includes('referat');
  const effectiveSectionForPersons = selectedReferat || (selectedSection && !showReferatyColumn ? selectedSection : null);

  useEffect(() => {
    setSelectedReferat(null);
  }, [selectedSection]);

  useEffect(() => {
    if (effectiveSectionForPersons) {
      loadPersons(effectiveSectionForPersons.id);
      loadOfficePositions(effectiveSectionForPersons.id);
    } else {
      setPersons([]);
      setOfficePositions([]);
    }
  }, [effectiveSectionForPersons]);

  const loadPersons = async (sectionId) => {
    try {
      const { data, error } = await supabaseAdmin
        .from('contact_persons')
        .select('*')
        .eq('section_id', sectionId)
        .order('order', { ascending: true });

      if (error) throw error;
      setPersons(data || []);
    } catch (error) {
      toast.error('Błąd podczas ładowania osób');
      console.error(error);
    }
  };

  const loadOfficePositions = async (sectionId) => {
    try {
      const { data, error } = await supabaseAdmin
        .from('contact_office_positions')
        .select('*')
        .eq('section_id', sectionId)
        .order('sort_order', { ascending: true });

      if (error) {
        const msg = String(error.message || error.details || error.code || '');
        if (/does not exist|42P01|42703|schema cache/i.test(msg)) {
          setOfficePositionsTableReady(false);
          setOfficePositions([]);
          return;
        }
        throw error;
      }
      setOfficePositionsTableReady(true);
      setOfficePositions(data || []);
    } catch (error) {
      console.error(error);
      toast.error('Błąd podczas ładowania stanowisk');
      setOfficePositions([]);
    }
  };

  const handleSectionSelect = (section) => {
    setSelectedSection(section);
  };

  const handleSectionAdd = () => {
    setEditingSection(null);
    setSectionFormData({
      name: '',
      order: topLevelSections.length > 0 ? Math.max(...topLevelSections.map((s) => s.order)) + 1 : 1,
      active: true,
      parent_id: null,
    });
    setShowSectionModal(true);
  };

  const handleReferatAdd = () => {
    if (!selectedSection) return;
    setEditingSection(null);
    const refOrders = referaty.map((r) => r.order);
    setSectionFormData({
      name: '',
      order: refOrders.length > 0 ? Math.max(...refOrders) + 1 : 1,
      active: true,
      parent_id: selectedSection.id,
    });
    setShowSectionModal(true);
  };

  const handleSectionEdit = (section) => {
    setEditingSection(section);
    setSectionFormData({
      name: section.name,
      order: section.order,
      active: section.active,
      parent_id: section.parent_id || null,
    });
    setShowSectionModal(true);
  };

  const handleReferatEdit = (section) => {
    handleSectionEdit(section);
  };

  const handleSectionDelete = async (sectionId) => {
    const section = sections.find((s) => s.id === sectionId);
    const isReferat = section?.parent_id != null;
    const msg = isReferat
      ? 'Czy na pewno chcesz usunąć ten referat? Wszystkie osoby i stanowiska w tym referacie również zostaną usunięte.'
      : 'Czy na pewno chcesz usunąć tę sekcję? Wszystkie podsekcje, osoby i stanowiska również zostaną usunięte.';
    if (!window.confirm(msg)) return;

    try {
      const { error } = await supabaseAdmin
        .from('contact_sections')
        .delete()
        .eq('id', sectionId);

      if (error) throw error;
      toast.success(isReferat ? 'Referat usunięty' : 'Sekcja usunięta');
      if (selectedSection?.id === sectionId) setSelectedSection(null);
      if (selectedReferat?.id === sectionId) setSelectedReferat(null);
      loadSections();
    } catch (error) {
      toast.error('Błąd podczas usuwania');
      console.error(error);
    }
  };

  const handleSectionSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!sectionFormData.name.trim()) {
        toast.error('Wypełnij nazwę sekcji');
        return;
      }

      const payload = {
        name: sectionFormData.name.trim(),
        order: sectionFormData.order,
        active: sectionFormData.active,
      };
      if (editingSection) {
        payload.updated_at = new Date().toISOString();
        const { error } = await supabaseAdmin
          .from('contact_sections')
          .update(payload)
          .eq('id', editingSection.id);
        if (error) throw error;
        toast.success(sectionFormData.parent_id ? 'Referat zaktualizowany' : 'Sekcja zaktualizowana');
      } else {
        if (sectionFormData.parent_id) payload.parent_id = sectionFormData.parent_id;
        const { error } = await supabaseAdmin
          .from('contact_sections')
          .insert(payload);
        if (error) throw error;
        toast.success(payload.parent_id ? 'Referat dodany' : 'Sekcja dodana');
      }

      setShowSectionModal(false);
      loadSections();
    } catch (error) {
      toast.error('Błąd podczas zapisywania sekcji');
      console.error(error);
    }
  };

  const handlePersonAdd = () => {
    if (!effectiveSectionForPersons) {
      toast.error(showReferatyColumn ? 'Wybierz referat, do którego dodać osobę' : 'Wybierz najpierw sekcję');
      return;
    }
    setEditingPerson(null);
    setPersonFormData({
      first_name: '',
      last_name: '',
      function_role: '',
      gender: 'male',
      phone_number: '',
      phone_extension: '',
      email: '',
      room_number: '',
      order: persons.length > 0 ? Math.max(...persons.map(p => p.order)) + 1 : 1,
      active: true,
    });
    setBlocks([{ 
      type: 'text', 
      text: '', 
      align: 'left',
      bold: false,
      italic: false,
      underline: false
    }]);
    setPhotoFile(null);
    setPhotoUrlInput('');
    setPhotoPreview(null);
    setShowPersonModal(true);
  };

  const handlePersonEdit = (person) => {
    setEditingPerson(person);
    setPersonFormData({
      first_name: person.first_name || '',
      last_name: person.last_name || '',
      function_role: person.function_role || '',
      gender: person.gender || 'male',
      phone_number: person.phone_number || '',
      phone_extension: person.phone_extension || '',
      email: person.email || '',
      room_number: person.room_number || '',
      order: person.order || 0,
      active: person.active !== false,
    });
    setBlocks(person.description && Array.isArray(person.description) ? person.description : [{ 
      type: 'text', 
      text: '', 
      align: 'left',
      bold: false,
      italic: false,
      underline: false
    }]);
    setPhotoFile(null);
    const existingPhotoUrl = person.photo_url ? getPublicImageUrl(person.photo_url) : '';
    setPhotoUrlInput(existingPhotoUrl || '');
    setPhotoPreview(existingPhotoUrl || null);
    setShowPersonModal(true);
  };

  const handlePersonDelete = async (personId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę osobę?')) {
      return;
    }

    try {
      const { error } = await supabaseAdmin
        .from('contact_persons')
        .delete()
        .eq('id', personId);

      if (error) throw error;
      toast.success('Osoba usunięta');
      loadPersons(effectiveSectionForPersons.id);
      loadOfficePositions(effectiveSectionForPersons.id);
    } catch (error) {
      toast.error('Błąd podczas usuwania osoby');
      console.error(error);
    }
  };

  const handleDeletePhoto = async () => {
    if (!editingPerson?.photo_url) {
      // Jeśli nie ma zapisanego zdjęcia, tylko usuń preview
      setPhotoFile(null);
      setPhotoPreview(null);
      return;
    }

    if (!window.confirm('Czy na pewno chcesz usunąć zdjęcie?')) {
      return;
    }

    try {
      // Wyciągnij nazwę pliku z URL (może być pełny URL lub tylko nazwa pliku)
      let filePath = editingPerson.photo_url;
      
      // Jeśli to pełny URL Supabase storage
      if (filePath.includes('/storage/v1/object/public/contact-photos/')) {
        filePath = filePath.split('/contact-photos/')[1];
      } else if (filePath.includes('/contact-photos/')) {
        // Jeśli zawiera bucket/ścieżkę w innej formie
        filePath = filePath.split('/contact-photos/')[1];
      } else if (filePath.includes('contact-photos/')) {
        filePath = filePath.split('contact-photos/')[1];
      }
      
      // Usuń query string jeśli istnieje (np. ?t=timestamp)
      if (filePath.includes('?')) {
        filePath = filePath.split('?')[0];
      }

      // Usuń z Storage tylko jeśli udało się wyodrębnić ścieżkę
      if (filePath && filePath.length > 0) {
        const { error: storageError } = await supabaseAdmin.storage
          .from('contact-photos')
          .remove([filePath]);

        // Ignoruj błąd jeśli plik nie istnieje w storage (może być już usunięty)
        if (storageError && storageError.message !== 'The resource was not found') {
          console.warn('Error deleting photo from storage:', storageError);
          // Nie przerywamy procesu - kontynuujemy usuwanie z bazy danych
        }
      }

      // Zaktualizuj w bazie danych
      const { error: dbError } = await supabaseAdmin
        .from('contact_persons')
        .update({ photo_url: null })
        .eq('id', editingPerson.id);

      if (dbError) throw dbError;

      // Zaktualizuj lokalny stan
      setPhotoPreview(null);
      setPhotoFile(null);
      setPhotoUrlInput('');

      // Zaktualizuj editingPerson, aby odzwierciedlić zmianę
      setEditingPerson({ ...editingPerson, photo_url: null });
      
      toast.success('Zdjęcie usunięte');
      loadPersons(effectiveSectionForPersons.id);
    } catch (error) {
      toast.error('Błąd podczas usuwania zdjęcia');
      console.error(error);
    }
  };

  const uploadPhoto = async (file) => {
    try {
      if (!canUploadContactPhotosFromPanel()) {
        throw new Error(getStorageUploadBlockedMessage());
      }

      if (!file.type.startsWith('image/')) {
        throw new Error('Wybierz plik obrazu');
      }

      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error('Obraz jest za duży. Maksymalny rozmiar: 5MB');
      }

      const fileExt = file.name.split('.').pop();
      const uuid = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const filePath = `${uuid}.${fileExt}`;

      setUploadingPhoto(true);

      const { error: uploadError } = await supabaseAdmin.storage
        .from('contact-photos')
        .upload(filePath, file, {
          contentType: file.type,
          upsert: false,
        });

      if (uploadError) {
        console.error('Upload error details:', uploadError);
        if (uploadError.message?.includes('not found')) {
          throw new Error('Bucket "contact-photos" nie istnieje. Utwórz go w Supabase Dashboard.');
        }
        throw uploadError;
      }

      const {
        data: { publicUrl },
      } = supabaseAdmin.storage.from('contact-photos').getPublicUrl(filePath);

      setUploadingPhoto(false);
      return publicUrl;
    } catch (error) {
      setUploadingPhoto(false);
      console.error('Error uploading photo:', error);
      toast.error(formatStorageUploadError(error));
      throw error;
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Wybierz plik obrazu');
      return;
    }

    if (!canUploadContactPhotosFromPanel()) {
      toast.error(getStorageUploadBlockedMessage(), { duration: 8000 });
      e.target.value = '';
      return;
    }

    setPhotoFile(file);
    setPhotoUrlInput('');
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handlePhotoUrlInputChange = (value) => {
    setPhotoUrlInput(value);
    setPhotoFile(null);
    const trimmed = value.trim();
    if (!trimmed) {
      setPhotoPreview(null);
      return;
    }
    if (isValidContactPhotoInput(trimmed)) {
      setPhotoPreview(normalizeContactPhotoUrl(trimmed));
    } else {
      setPhotoPreview(null);
    }
  };

  const handlePersonSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!personFormData.first_name.trim() || !personFormData.last_name.trim()) {
        toast.error('Wypełnij imię i nazwisko');
        return;
      }

      if (!effectiveSectionForPersons) {
        toast.error('Wybierz sekcję lub referat');
        return;
      }

      let photoUrl = editingPerson?.photo_url || null;

      if (photoFile) {
        setUploadingPhoto(true);
        try {
          photoUrl = await uploadPhoto(photoFile);
        } catch (uploadError) {
          toast.error('Błąd podczas przesyłania zdjęcia');
          setUploadingPhoto(false);
          return;
        }
        setUploadingPhoto(false);
      } else {
        const trimmedLink = (photoUrlInput || '').trim();
        if (trimmedLink) {
          if (!isValidContactPhotoInput(trimmedLink)) {
            toast.error(
              'Podaj publiczny link z Supabase (bucket contact-photos) lub samą nazwę pliku, np. wojt.jpg'
            );
            return;
          }
          photoUrl = normalizeContactPhotoUrl(trimmedLink);
        } else {
          photoUrl = null;
        }
      }

      if (editingPerson) {
        const { error } = await supabaseAdmin
          .from('contact_persons')
          .update({
            first_name: personFormData.first_name.trim(),
            last_name: personFormData.last_name.trim(),
            function_role: personFormData.function_role.trim() || null,
            gender: personFormData.gender,
            phone_number: personFormData.phone_number.trim() || null,
            phone_extension: personFormData.phone_extension.trim() || null,
            email: personFormData.email.trim() || null,
            room_number: personFormData.room_number.trim() || null,
            photo_url: photoUrl,
            description: blocks,
            order: personFormData.order,
            active: personFormData.active,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingPerson.id);

        if (error) throw error;
        toast.success('Osoba zaktualizowana');
      } else {
        const { error } = await supabaseAdmin
          .from('contact_persons')
          .insert({
            section_id: effectiveSectionForPersons.id,
            first_name: personFormData.first_name.trim(),
            last_name: personFormData.last_name.trim(),
            function_role: personFormData.function_role.trim() || null,
            gender: personFormData.gender,
            phone_number: personFormData.phone_number.trim() || null,
            phone_extension: personFormData.phone_extension.trim() || null,
            email: personFormData.email.trim() || null,
            room_number: personFormData.room_number.trim() || null,
            photo_url: photoUrl,
            description: blocks,
            order: personFormData.order,
            active: personFormData.active,
          });

        if (error) throw error;
        toast.success('Osoba dodana');
      }

      setShowPersonModal(false);
      loadPersons(effectiveSectionForPersons.id);
      loadOfficePositions(effectiveSectionForPersons.id);
    } catch (error) {
      toast.error('Błąd podczas zapisywania osoby');
      console.error(error);
    }
  };

  const moveSectionOrder = async (sectionId, direction) => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return;
    const siblings = section.parent_id
      ? sections.filter((s) => s.parent_id === section.parent_id).sort((a, b) => a.order - b.order)
      : topLevelSections.slice().sort((a, b) => a.order - b.order);
    const currentIndex = siblings.findIndex((s) => s.id === sectionId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= siblings.length) return;
    const otherSection = siblings[newIndex];
    const tempOrder = section.order;
    section.order = otherSection.order;
    otherSection.order = tempOrder;
    try {
      await supabaseAdmin.from('contact_sections').update({ order: section.order }).eq('id', section.id);
      await supabaseAdmin.from('contact_sections').update({ order: otherSection.order }).eq('id', otherSection.id);
      loadSections();
    } catch (error) {
      toast.error('Błąd podczas zmiany kolejności');
      console.error(error);
    }
  };

  const movePersonOrder = async (personId, direction) => {
    const person = persons.find(p => p.id === personId);
    if (!person) return;

    const currentIndex = persons.findIndex(p => p.id === personId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= persons.length) return;

    const otherPerson = persons[newIndex];
    const tempOrder = person.order;
    person.order = otherPerson.order;
    otherPerson.order = tempOrder;

    try {
      await supabaseAdmin
        .from('contact_persons')
        .update({ order: person.order })
        .eq('id', person.id);
      await supabaseAdmin
        .from('contact_persons')
        .update({ order: otherPerson.order })
        .eq('id', otherPerson.id);
      
      loadPersons(effectiveSectionForPersons.id);
    } catch (error) {
      toast.error('Błąd podczas zmiany kolejności');
      console.error(error);
    }
  };

  const handleOfficeAdd = () => {
    if (!effectiveSectionForPersons) {
      toast.error(showReferatyColumn ? 'Wybierz referat' : 'Wybierz sekcję');
      return;
    }
    if (!officePositionsTableReady) {
      toast.error(
        'Brak tabeli stanowisk. Uruchom migrację SQL: supabase/migrations/20260331120000_contact_office_positions.sql',
      );
      return;
    }
    setEditingOffice(null);
    setOfficeFormData({
      title: '',
      phone_number: '',
      phone_extension: '',
      email: '',
      sort_order: officePositions.length > 0 ? Math.max(...officePositions.map((o) => o.sort_order ?? 0)) + 1 : 1,
      active: true,
    });
    setShowOfficeModal(true);
  };

  const handleOfficeEdit = (row) => {
    setEditingOffice(row);
    setOfficeFormData({
      title: row.title || '',
      phone_number: row.phone_number || '',
      phone_extension: row.phone_extension || '',
      email: row.email || '',
      sort_order: row.sort_order ?? 0,
      active: row.active !== false,
    });
    setShowOfficeModal(true);
  };

  const handleOfficeDelete = async (id) => {
    if (!window.confirm('Usunąć to stanowisko?')) return;
    try {
      const { error } = await supabaseAdmin.from('contact_office_positions').delete().eq('id', id);
      if (error) throw error;
      toast.success('Stanowisko usunięte');
      loadOfficePositions(effectiveSectionForPersons.id);
    } catch (error) {
      toast.error('Błąd podczas usuwania');
      console.error(error);
    }
  };

  const handleOfficeSubmit = async (e) => {
    e.preventDefault();
    if (!officePositionsTableReady) return;
    const title = officeFormData.title.trim();
    if (!title) {
      toast.error('Podaj nazwę stanowiska');
      return;
    }
    if (!effectiveSectionForPersons) return;

    const emailTrim = officeFormData.email.trim();
    if (emailTrim && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      toast.error('Nieprawidłowy adres e-mail');
      return;
    }

    try {
      const now = new Date().toISOString();
      const row = {
        title,
        phone_number: officeFormData.phone_number.trim() || null,
        phone_extension: officeFormData.phone_extension.trim() || null,
        email: emailTrim || null,
        sort_order: Number(officeFormData.sort_order) || 0,
        active: officeFormData.active,
        updated_at: now,
      };
      if (editingOffice) {
        const { error } = await supabaseAdmin.from('contact_office_positions').update(row).eq('id', editingOffice.id);
        if (error) throw error;
        toast.success('Stanowisko zaktualizowane');
      } else {
        const { error } = await supabaseAdmin
          .from('contact_office_positions')
          .insert({ ...row, section_id: effectiveSectionForPersons.id, created_at: now });
        if (error) throw error;
        toast.success('Stanowisko dodane');
      }
      setShowOfficeModal(false);
      loadOfficePositions(effectiveSectionForPersons.id);
    } catch (error) {
      toast.error(error.message || 'Błąd zapisu stanowiska');
      console.error(error);
    }
  };

  const moveOfficeOrder = async (rowId, direction) => {
    const row = officePositions.find((o) => o.id === rowId);
    if (!row) return;
    const currentIndex = officePositions.findIndex((o) => o.id === rowId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= officePositions.length) return;
    const other = officePositions[newIndex];
    const a = row.sort_order ?? 0;
    const b = other.sort_order ?? 0;
    try {
      await supabaseAdmin.from('contact_office_positions').update({ sort_order: b, updated_at: new Date().toISOString() }).eq('id', row.id);
      await supabaseAdmin.from('contact_office_positions').update({ sort_order: a, updated_at: new Date().toISOString() }).eq('id', other.id);
      loadOfficePositions(effectiveSectionForPersons.id);
    } catch (error) {
      toast.error('Błąd podczas zmiany kolejności');
      console.error(error);
    }
  };

  if (loading) {
    return <div className="loading">Ładowanie...</div>;
  }

  return (
    <div className="news-management contact-management">
      <div className="page-header">
        <h2>Zarządzanie kontaktami</h2>
      </div>

      <div className="card contact-office-header-admin" style={{ marginTop: '20px' }}>
        <h3>Adres urzędu gminy (nad sekcjami w aplikacji mobilnej)</h3>
        <p style={{ marginTop: 8, marginBottom: 12, fontSize: 14, color: '#374151' }}>
          Ten tekst pojawi się na samej górze zakładki Kontakt w aplikacji, powyżej listy sekcji (np. Władze gminy, Rada gminy).
          Możesz wpisać adres, godziny pracy lub krótką wzmiankę — kilka linii jest dozwolonych (Enter).
        </p>
        <textarea
          className="contact-office-header-textarea"
          rows={5}
          value={contactOfficeHeader}
          onChange={(e) => setContactOfficeHeader(e.target.value)}
          placeholder={'np. Urząd Gminy Bielawy\nul. …\n99-423 Bielawy'}
        />
        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSaveContactOfficeHeader}
            disabled={savingContactOfficeHeader}
          >
            {savingContactOfficeHeader ? 'Zapisywanie…' : 'Zapisz adres / nagłówek'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '20px', marginTop: '20px' }}>
        {/* Left column: Top-level sections */}
        <div className="card" style={{ flex: '0 0 280px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3>Sekcje</h3>
            <button className="btn btn-primary" onClick={handleSectionAdd}>
              + Dodaj sekcję
            </button>
          </div>
          <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {topLevelSections.map((section, idx) => (
              <div
                key={section.id}
                style={{
                  padding: '12px',
                  marginBottom: '8px',
                  border: selectedSection?.id === section.id ? '2px solid #007AFF' : '1px solid #ddd',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: selectedSection?.id === section.id ? '#f0f8ff' : '#fff',
                }}
                onClick={() => handleSectionSelect(section)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{section.name}</div>
                    <div style={{ fontSize: '12px', color: '#000' }}>
                      Kolejność: {section.order} | {section.active ? 'Aktywna' : 'Nieaktywna'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); moveSectionOrder(section.id, 'up'); }} disabled={idx === 0}>↑</button>
                    <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); moveSectionOrder(section.id, 'down'); }} disabled={idx === topLevelSections.length - 1}>↓</button>
                    <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); handleSectionEdit(section); }}>Edytuj</button>
                    <button className="btn btn-sm btn-danger" onClick={(e) => { e.stopPropagation(); handleSectionDelete(section.id); }}>Usuń</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Middle column: Referaty (only when selected section has children) */}
        {showReferatyColumn && (
          <div className="card" style={{ flex: '0 0 280px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3>Referaty</h3>
              <button className="btn btn-primary" onClick={handleReferatAdd}>
                + Dodaj referat
              </button>
            </div>
            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {referaty.sort((a, b) => a.order - b.order).map((ref, idx) => (
                <div
                  key={ref.id}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    border: selectedReferat?.id === ref.id ? '2px solid #007AFF' : '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: selectedReferat?.id === ref.id ? '#f0f8ff' : '#fff',
                  }}
                  onClick={() => setSelectedReferat(ref)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{ref.name}</div>
                      <div style={{ fontSize: '12px', color: '#000' }}>
                        Kolejność: {ref.order} | {ref.active ? 'Aktywna' : 'Nieaktywna'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); moveSectionOrder(ref.id, 'up'); }} disabled={idx === 0}>↑</button>
                      <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); moveSectionOrder(ref.id, 'down'); }} disabled={idx === referaty.length - 1}>↓</button>
                      <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); handleReferatEdit(ref); }}>Edytuj</button>
                      <button className="btn btn-sm btn-danger" onClick={(e) => { e.stopPropagation(); handleSectionDelete(ref.id); }}>Usuń</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Right column: Persons */}
        <div className="card" style={{ flex: 1 }}>
          {effectiveSectionForPersons ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3>Osoby – {effectiveSectionForPersons.name}</h3>
                <button className="btn btn-primary" onClick={handlePersonAdd}>
                  + Dodaj osobę
                </button>
              </div>
              <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {persons.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#000' }}>
                    Brak osób w tej sekcji
                  </div>
                ) : (
                  persons.map((person) => (
                    <div
                      key={person.id}
                      style={{
                        padding: '12px',
                        marginBottom: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 'bold' }}>
                          {person.first_name} {person.last_name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#000' }}>
                          {person.function_role || 'Brak funkcji'} | Kolejność: {person.order} | {person.active ? 'Aktywna' : 'Nieaktywna'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          className="btn btn-sm"
                          onClick={() => movePersonOrder(person.id, 'up')}
                          disabled={persons.findIndex(p => p.id === person.id) === 0}
                        >
                          ↑
                        </button>
                        <button
                          className="btn btn-sm"
                          onClick={() => movePersonOrder(person.id, 'down')}
                          disabled={persons.findIndex(p => p.id === person.id) === persons.length - 1}
                        >
                          ↓
                        </button>
                        <button
                          className="btn btn-sm"
                          onClick={() => handlePersonEdit(person)}
                        >
                          Edytuj
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handlePersonDelete(person.id)}
                        >
                          Usuń
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="contact-office-positions-block">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 12,
                    marginTop: 20,
                    paddingTop: 16,
                    borderTop: '1px solid #e5e7eb',
                  }}
                >
                  <h3 style={{ margin: 0 }}>Pozostałe stanowiska</h3>
                  <button type="button" className="btn btn-primary" onClick={handleOfficeAdd}>
                    + Dodaj stanowisko
                  </button>
                </div>
                <p style={{ fontSize: 13, color: '#374151', marginBottom: 12 }}>
                  Wpis bez przypisanej osoby — wyświetlane pod listą osób w aplikacji. Telefon i e-mail są opcjonalne; przy
                  telefonie możesz podać osobno numer i wewnętrzny.
                </p>
                {!officePositionsTableReady && (
                  <div
                    role="alert"
                    style={{
                      marginBottom: 12,
                      padding: '12px 14px',
                      background: '#fef3c7',
                      border: '2px solid #f59e0b',
                      borderRadius: 8,
                      color: '#78350f',
                      fontSize: 14,
                    }}
                  >
                    <strong>Brak tabeli w bazie.</strong> Wykonaj migrację{' '}
                    <code>supabase/migrations/20260331120000_contact_office_positions.sql</code> w Supabase SQL Editor.
                  </div>
                )}
                <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                  {officePositions.length === 0 ? (
                    <div style={{ color: '#6b7280', fontSize: 14 }}>Brak stanowisk — dodaj pierwsze powyżej.</div>
                  ) : (
                    officePositions.map((o, idx) => (
                      <div
                        key={o.id}
                        style={{
                          padding: '12px',
                          marginBottom: 8,
                          border: '1px solid #ddd',
                          borderRadius: 8,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 'bold' }}>{o.title}</div>
                          <div style={{ fontSize: 12, color: '#000' }}>
                            {[o.phone_number, o.phone_extension ? `wew. ${o.phone_extension}` : null, o.email]
                              .filter(Boolean)
                              .join(' · ') || '—'}
                            {' · '}
                            kolejność: {o.sort_order ?? 0} · {o.active ? 'aktywne' : 'wyłączone'}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => moveOfficeOrder(o.id, 'up')}
                            disabled={idx === 0}
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => moveOfficeOrder(o.id, 'down')}
                            disabled={idx === officePositions.length - 1}
                          >
                            ↓
                          </button>
                          <button type="button" className="btn btn-sm" onClick={() => handleOfficeEdit(o)}>
                            Edytuj
                          </button>
                          <button type="button" className="btn btn-sm btn-danger" onClick={() => handleOfficeDelete(o.id)}>
                            Usuń
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#000' }}>
              {showReferatyColumn ? 'Wybierz referat, aby zobaczyć i dodawać osoby' : 'Wybierz sekcję, aby zobaczyć osoby'}
            </div>
          )}
        </div>
      </div>

      {/* Section Modal */}
      {showSectionModal && (
        <div className="modal-overlay" onClick={() => setShowSectionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{editingSection ? 'Edytuj sekcję' : 'Dodaj sekcję'}</h3>
            <form onSubmit={handleSectionSubmit}>
              <div className="form-group">
                <label className="form-label">Nazwa sekcji *</label>
                <input
                  type="text"
                  className="form-input"
                  value={sectionFormData.name}
                  onChange={(e) => setSectionFormData({ ...sectionFormData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Kolejność</label>
                <input
                  type="number"
                  className="form-input"
                  value={sectionFormData.order}
                  onChange={(e) => setSectionFormData({ ...sectionFormData, order: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  <input
                    type="checkbox"
                    checked={sectionFormData.active}
                    onChange={(e) => setSectionFormData({ ...sectionFormData, active: e.target.checked })}
                  />
                  {' '}Aktywna
                </label>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingSection ? 'Zaktualizuj' : 'Dodaj'}
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => setShowSectionModal(false)}
                >
                  Anuluj
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Person Modal */}
      {showPersonModal && effectiveSectionForPersons && (
        <div className="modal-overlay" onClick={() => setShowPersonModal(false)}>
          <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h3>{editingPerson ? 'Edytuj osobę' : 'Dodaj osobę'}</h3>
            <form onSubmit={handlePersonSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Imię *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={personFormData.first_name}
                    onChange={(e) => setPersonFormData({ ...personFormData, first_name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Nazwisko *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={personFormData.last_name}
                    onChange={(e) => setPersonFormData({ ...personFormData, last_name: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Funkcja / Rola</label>
                <input
                  type="text"
                  className="form-input"
                  value={personFormData.function_role}
                  onChange={(e) => setPersonFormData({ ...personFormData, function_role: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Płeć (dla placeholder avatar)</label>
                <select
                  className="form-input"
                  value={personFormData.gender}
                  onChange={(e) => setPersonFormData({ ...personFormData, gender: e.target.value })}
                >
                  <option value="male">Mężczyzna</option>
                  <option value="female">Kobieta</option>
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Telefon</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={personFormData.phone_number}
                    onChange={(e) => setPersonFormData({ ...personFormData, phone_number: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Wewnętrzny</label>
                  <input
                    type="text"
                    className="form-input"
                    value={personFormData.phone_extension}
                    onChange={(e) => setPersonFormData({ ...personFormData, phone_extension: e.target.value })}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={personFormData.email}
                    onChange={(e) => setPersonFormData({ ...personFormData, email: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Pokój</label>
                  <input
                    type="text"
                    className="form-input"
                    value={personFormData.room_number}
                    onChange={(e) => setPersonFormData({ ...personFormData, room_number: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Zdjęcie (opcjonalne)</label>
                <p style={{ fontSize: 13, color: '#374151', margin: '0 0 10px' }}>
                  Zalecane: wgraj plik ręcznie w Supabase → Storage → <strong>contact-photos</strong> (bucket
                  publiczny), skopiuj publiczny URL i wklej poniżej. Nie wymaga uploadu z panelu ani klucza service
                  role.
                </p>
                <label className="form-label" style={{ fontSize: 14, fontWeight: 500 }}>
                  Link do zdjęcia w Storage
                </label>
                <input
                  type="url"
                  className="form-input"
                  value={photoUrlInput}
                  onChange={(e) => handlePhotoUrlInputChange(e.target.value)}
                  placeholder="https://…supabase.co/storage/v1/object/public/contact-photos/wojt.jpg"
                  style={{ marginBottom: 12 }}
                />
                <label className="form-label" style={{ fontSize: 14, fontWeight: 500, color: '#6b7280' }}>
                  Lub wgraj z komputera (tylko przy wyłączonym proxy + service role w admin/.env)
                </label>
                {getSupabaseConnectionMode() === 'direct' && !canUploadContactPhotosFromPanel() && (
                  <p style={{ fontSize: 13, color: '#b45309', margin: '0 0 8px', lineHeight: 1.45 }}>
                    Brak <code>REACT_APP_SUPABASE_SERVICE_ROLE_KEY</code> w <code>admin/.env</code> — upload zablokuje
                    RLS. Dodaj klucz service_role i zrestartuj <code>npm start</code>, albo użyj pola z linkiem powyżej.
                  </p>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  disabled={!canUploadContactPhotosFromPanel()}
                  style={{ marginBottom: '8px' }}
                />
                {photoPreview && (
                  <div style={{ marginTop: '8px' }}>
                    <img
                      src={photoPreview}
                      alt="Preview"
                      style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '50%' }}
                    />
                    <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                      {photoFile && (
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => {
                            setPhotoFile(null);
                            const prev = editingPerson?.photo_url
                              ? getPublicImageUrl(editingPerson.photo_url)
                              : '';
                            setPhotoUrlInput(prev);
                            setPhotoPreview(prev || null);
                          }}
                        >
                          Usuń nowe zdjęcie
                        </button>
                      )}
                      {editingPerson?.photo_url && !photoFile && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={handleDeletePhoto}
                          disabled={uploadingPhoto}
                        >
                          Usuń zdjęcie z bazy
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {uploadingPhoto && <div>Przesyłanie zdjęcia...</div>}
              </div>

              <div className="form-group">
                <label className="form-label">Opis (opcjonalne)</label>
                <BlockEditor
                  blocks={blocks}
                  onBlocksChange={setBlocks}
                  onImageUpload={async (file) => {
                    if (!canUploadContactPhotosFromPanel()) {
                      toast.error(getStorageUploadBlockedMessage(), { duration: 8000 });
                      throw new Error(getStorageUploadBlockedMessage());
                    }
                    try {
                      const fileExt = file.name.split('.').pop();
                      const fileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;

                      const { error: uploadError } = await supabaseAdmin.storage
                        .from('contact-photos')
                        .upload(fileName, file, {
                          contentType: file.type,
                          upsert: false,
                        });

                      if (uploadError) {
                        throw new Error(formatStorageUploadError(uploadError));
                      }

                      const {
                        data: { publicUrl },
                      } = supabaseAdmin.storage.from('contact-photos').getPublicUrl(fileName);

                      return publicUrl;
                    } catch (error) {
                      console.error('Error uploading image:', error);
                      toast.error(formatStorageUploadError(error));
                      throw error;
                    }
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Kolejność</label>
                  <input
                    type="number"
                    className="form-input"
                    value={personFormData.order}
                    onChange={(e) => setPersonFormData({ ...personFormData, order: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <input
                      type="checkbox"
                      checked={personFormData.active}
                      onChange={(e) => setPersonFormData({ ...personFormData, active: e.target.checked })}
                    />
                    {' '}Aktywna
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={uploadingPhoto}>
                  {uploadingPhoto ? 'Zapisywanie...' : editingPerson ? 'Zaktualizuj' : 'Dodaj'}
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => setShowPersonModal(false)}
                >
                  Anuluj
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showOfficeModal && effectiveSectionForPersons && (
        <div className="modal-overlay" onClick={() => setShowOfficeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{editingOffice ? 'Edytuj stanowisko' : 'Dodaj stanowisko urzędnicze'}</h3>
            <form onSubmit={handleOfficeSubmit}>
              <div className="form-group">
                <label className="form-label">Nazwa stanowiska *</label>
                <input
                  type="text"
                  className="form-input"
                  value={officeFormData.title}
                  onChange={(e) => setOfficeFormData({ ...officeFormData, title: e.target.value })}
                  placeholder="np. Referat obsługi interesantów"
                  required
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">Nr telefonu służbowego (opcjonalnie)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={officeFormData.phone_number}
                    onChange={(e) => setOfficeFormData({ ...officeFormData, phone_number: e.target.value })}
                    placeholder="np. 22 123 45 67"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Nr wewnętrzny (opcjonalnie)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={officeFormData.phone_extension}
                    onChange={(e) => setOfficeFormData({ ...officeFormData, phone_extension: e.target.value })}
                    placeholder="np. 234"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">E-mail służbowy (opcjonalnie)</label>
                <input
                  type="email"
                  className="form-input"
                  value={officeFormData.email}
                  onChange={(e) => setOfficeFormData({ ...officeFormData, email: e.target.value })}
                  placeholder="np. referat@urzad.pl"
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">Kolejność (sort_order)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={officeFormData.sort_order}
                    onChange={(e) =>
                      setOfficeFormData({ ...officeFormData, sort_order: parseInt(e.target.value, 10) || 0 })
                    }
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <input
                      type="checkbox"
                      checked={officeFormData.active}
                      onChange={(e) => setOfficeFormData({ ...officeFormData, active: e.target.checked })}
                    />{' '}
                    Aktywne (widoczne w aplikacji)
                  </label>
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingOffice ? 'Zapisz' : 'Dodaj'}
                </button>
                <button type="button" className="btn" onClick={() => setShowOfficeModal(false)}>
                  Anuluj
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
